import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { t } from "../../context/translations";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FaGlobe } from "react-icons/fa";
import { Helmet } from "react-helmet";

const LanguagePage = () => {
  const { language, setLanguage } = useLanguage();
  const { isDarkMode } = useDarkMode();

  const languages = [
    { id: "english", label: "English", nativeLabel: "English", flag: "🇺🇸" },
    { id: "hindi", label: "हिन्दी", nativeLabel: "Hindi", flag: "🇮🇳" },
    { id: "arabic", label: "العربية", nativeLabel: "Arabic", flag: "🇸🇦" },
  ];

  return (
    <>

<Helmet>

  {/* Title */}
  <title>Language Settings | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="Change your preferred language in Seo Up Rank dashboard to personalize your AI powered SEO tools and user experience."
  />

  {/* Private Page */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/language" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Language Settings" />
  <meta
    property="og:description"
    content="Select your preferred language for Seo Up Rank dashboard and SEO tools."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/language" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Language Settings" />
  <meta
    name="twitter:description"
    content="Update your language preferences in the Seo Up Rank dashboard."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"Language Settings - Seo Up Rank",
      "url":"https://novarsis.ai/language",
      "description":"User language settings page in Seo Up Rank dashboard."
    }
    `}
  </script>

</Helmet>
    <div className="flex items-center justify-center min-h-[calc(100vh-120px)] p-4">
      <div className={`rounded-xl shadow-lg p-5 sm:p-6 w-full max-w-sm ${isDarkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"}`}>
        {/* Header */}
        <header className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <FaGlobe className="text-2xl sm:text-3xl text-[#008300]" />
            <h1 className="text-2xl sm:text-3xl font-bold text-[#008300]">{t("language")}</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ">Choose your preferred language</p>
        </header>

        {/* Language Options */}
        <section className="space-y-2">
          {languages.map((lang, idx) => (
            <motion.button
              key={lang.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setLanguage(lang.id)}
              className={`w-full p-3 sm:p-4 rounded-lg border transition-all text-left flex items-center justify-between ${
                language === lang.id
                  ? isDarkMode
                    ? "bg-green-900/30 border-[#008300] shadow-md"
                    : "bg-green-50 border-[#008300] shadow-md"
                  : isDarkMode
                  ? "bg-gray-800 border-gray-700 hover:border-gray-600"
                  : "bg-gray-50 border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-xl sm:text-2xl flex-shrink-0">{lang.flag}</span>
                <div className="min-w-0">
                  <div className="text-sm sm:text-base font-semibold truncate">{lang.label}</div>
                  <div className="text-xs text-gray-500 truncate">{lang.nativeLabel}</div>
                </div>
              </div>

              <input
                type="radio"
                name="language"
                checked={language === lang.id}
                onChange={() => setLanguage(lang.id)}
                className="w-4 h-4 sm:w-5 sm:h-5 accent-[#008300] cursor-pointer flex-shrink-0 ml-2"
              />
            </motion.button>
          ))}
        </section>

        {/* Footer Note */}
        <p className="mt-5 text-xs sm:text-xs text-center text-gray-500 mt-5">
          Changes apply instantly
        </p>
      </div>
    </div></>
  );
};

export default LanguagePage;